// src/pages/Orders/ApplyCreditModal.jsx
import { useState, useEffect } from 'react';
import creditService from '../../services/credit.service';

const fmt = (a) => `₹${Number(a || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const ApplyCreditModal = ({ dealerId, payment, onClose, onApplied, setToast }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const remaining = parseFloat(payment.amount || 0) - parseFloat(payment.totalConfirmed || 0);

  useEffect(() => {
    creditService.getDealerCreditSummary(dealerId)
      .then(res => { if (res.success) setSummary(res.data); })
      .catch(() => setToast({ message: 'Failed to load credit balance', type: 'error' }))
      .finally(() => setLoading(false));
  }, [dealerId]);

  const availableForSource = () => {
    if (!summary) return 0;
    if (source === 'DEALER') return summary.dealer.balance;
    return summary.subDealers.find(s => String(s.ownerId) === source)?.balance || 0;
  };

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!source) return setToast({ message: 'Select a credit source', type: 'error' });
    if (!amt || amt <= 0) return setToast({ message: 'Enter a valid amount', type: 'error' });
    if (amt > availableForSource()) return setToast({ message: 'Amount exceeds available credit', type: 'error' });
    if (amt > remaining) return setToast({ message: 'Amount exceeds order balance due', type: 'error' });

    setIsSubmitting(true);
    try {
      const ownerType = source === 'DEALER' ? 'DEALER' : 'SUB_DEALER';
      const ownerId = source === 'DEALER' ? dealerId : parseInt(source);
      const res = await creditService.applyCredit({ ownerType, ownerId, paymentId: payment.id, amount: amt });
      if (res.success) { setToast({ message: 'Credit applied!', type: 'success' }); onApplied(); }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to apply credit', type: 'error' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="pay-modal-overlay" onClick={onClose}>
      <div className="pay-modal" onClick={e => e.stopPropagation()}>
        <div className="pay-modal-header">
          <h5 className="pay-modal-title">Apply Credit</h5>
          <button className="pay-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="pay-modal-body">
          {loading ? <p>Loading balances...</p> : (
            <>
              <div className="pay-field" style={{ marginBottom: 12 }}>
                <label className="pay-label">Credit Source</label>
                <select className="pay-input" value={source} onChange={e => setSource(e.target.value)}>
                  <option value="">-- Select Source --</option>
                  <option value="DEALER">Dealer balance — {fmt(summary.dealer.balance)}</option>
                  {summary.subDealers.map(sd => (
                    <option key={sd.ownerId} value={sd.ownerId}>{sd.name} — {fmt(sd.balance)}</option>
                  ))}
                </select>
              </div>
              <div className="pay-field" style={{ marginBottom: 12 }}>
                <label className="pay-label">Amount to Apply (Order balance due: {fmt(remaining)})</label>
                <input type="number" className="pay-input" value={amount} onChange={e => setAmount(e.target.value)}
                  max={Math.min(remaining, availableForSource())} min="0.01" step="0.01" />
              </div>
            </>
          )}
        </div>
        <div className="pay-modal-footer">
          <button className="pay-btn-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button className="pay-btn-primary" onClick={handleSubmit} disabled={isSubmitting || loading}>
            {isSubmitting ? 'Applying...' : '✓ Apply Credit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplyCreditModal;